import { Injectable } from '@angular/core';
import { Observable, from, BehaviorSubject, forkJoin, Subject, timer, retry, throwError, defer } from 'rxjs';
import * as platformClient from 'purecloud-platform-client-v2';
import { AppConfigService } from './app-config.service';

// Keys for localStorage
const LANGUAGE_KEY = 'gc_language';
const ENV_KEY = 'gc_environment'

@Injectable({
  providedIn: 'root'
})
export class GenesysCloudService {
  private topics: { [key: string]: Subject<any> } = {};
  private client = platformClient.ApiClient.instance;
  private usersApi = new platformClient.UsersApi();
  private presenceApi = new platformClient.PresenceApi();
  private tokensApi = new platformClient.TokensApi();
  private notificationsApi = new platformClient.NotificationsApi();
  private conversationApi = new platformClient.ConversationsApi();
  private conversationAnalyticsApi = new platformClient.AnalyticsApi();

  // Authorization values
  language: string = 'en-us';
  environment: string = '';
  accessToken = '';
  userDetails = new BehaviorSubject<platformClient.Models.UserMe | undefined>(undefined);
  public userDetails$ = this.userDetails.asObservable();

  // Cache for presence definitions
  offlinePresenceId = '';

  constructor(private appConfigService: AppConfigService) {
    // Try to get saved language and environment information from localstorage
    this.language = localStorage.getItem(LANGUAGE_KEY) || this.language;
    this.environment = localStorage.getItem(ENV_KEY) || this.environment;
  }

  initialize(accessToken: string): Observable<platformClient.Models.UserMe> {
    this.client.setPersistSettings(true);
    if (this.appConfigService.genesysConfiguration?.environment) this.client.setEnvironment(this.appConfigService.genesysConfiguration?.environment);
    this.client.setAccessToken(accessToken)
    return from(this.getUserMe())
  }

  setLanguage(language: string | null): void {
    if (language) {
      this.language = language;
      localStorage.setItem(LANGUAGE_KEY, this.language);
    }
  }

  setEnvironment(environment: string | null): void {
    if (environment) {
      this.environment = environment;
      localStorage.setItem(ENV_KEY, this.environment);
    }
  }

  getUserMe(): Observable<platformClient.Models.UserMe> {
    return from(this.usersApi.getUsersMe({
      expand: ['routingStatus', 'presence'],
    }));
  }

  getUserPresence(presenceId: string): Observable<platformClient.Models.OrganizationPresence> {
    return from(this.presenceApi.getPresencedefinition(presenceId));
  }


  logoutUser(userId: string): Observable<any> {
    return forkJoin({
      deletetoken: from(this.tokensApi.deleteToken(userId)),
      setOffline: from(this.presenceApi.patchUserPresence(userId, 'PURECLOUD', {
        presenceDefinition: { id: this.offlinePresenceId }
      })),
    });
  }

  async initializeWebSocketConnection(): Promise<boolean> {
    const userId = localStorage.getItem('genesysUserId');
    if (!userId) {
      console.error('UserId not found in localStorage');
      return Promise.reject('UserId not found');
    }

    try {
      const notificationChannel = await this.notificationsApi.postNotificationsChannels();
      if (!notificationChannel.connectUri || !notificationChannel.id) {
        throw new Error('Notification channel details are incomplete');
      }

      const webSocket = new WebSocket(notificationChannel.connectUri);
      const topicsToSubscribe = [
        `v2.users.${userId}.conversations.emails`,
        `v2.users.${userId}.conversations.calls`,
        `v2.users.${userId}.presence`,
      ];

      topicsToSubscribe.forEach(topic => {
        if (!this.topics[topic]) {
          this.topics[topic] = new Subject<any>();
        }
      });

      const body = topicsToSubscribe.map(topic => ({ id: topic }));

      webSocket.onmessage = (message) => {
        const notification = JSON.parse(message.data);
        const topic = notification.topicName;
        if (this.topics[topic]) {
          this.topics[topic].next(notification);
        }
      };

      await this.notificationsApi.putNotificationsChannelSubscriptions(notificationChannel.id, body);
      return true;
    } catch (error) {
      console.error('Error initializing WebSocket connection:', error);
      return false;
    }
  }

  subscribeToTopic(partialTopic: string): Observable<any> {
    let userId = localStorage.getItem('genesysUserId')
    const fullTopic = `v2.users.${userId}.${partialTopic}`;
    if (!this.topics[fullTopic]) {
      this.topics[fullTopic] = new Subject<any>();
    }
    return this.topics[fullTopic].asObservable();
  }

  getMyCallConversations(): Observable<platformClient.Models.CallConversationEntityListing> {
    return from(this.conversationApi.getConversationsCalls());
  }

  getConversationById(conversationId: string): Observable<platformClient.Models.Conversation> {
    const maxRetries    = 5;
    const baseDelayMs   = 4_000; // 1s
  
    return defer(()=>this.conversationApi.getConversation(conversationId)).pipe(
      retry({
        count: maxRetries,
        delay: (error, retryAttempt) => {
          // if it’s not a 404, stop retrying by throwing immediately
          if (error?.status !== 404) {
            return throwError(() => error);
          }
          // else wait 1s, 2s, 4s, 8s… before retry
          return timer(baseDelayMs * Math.pow(2, retryAttempt - 1));
        }
      })
    );
  }

  
  getCallConversationById(callConversationId: string): Observable<platformClient.Models.CallConversation> {
    const maxRetries    = 5;
    const baseDelayMs   = 4_000; // 1s
  
    return defer(()=>this.conversationApi.getConversationsCall(callConversationId)).pipe(
      retry({
        count: maxRetries,
        delay: (error, retryAttempt) => {
          // if it’s not a 404, stop retrying by throwing immediately
          if (error?.status !== 404) {
            return throwError(() => error);
          }
          console.log(`Retrying... Attempt ${retryAttempt} after ${baseDelayMs * Math.pow(2, retryAttempt - 1)}ms`);
          // else wait 1s, 2s, 4s, 8s… before retry
          return timer(baseDelayMs * Math.pow(2, retryAttempt - 1));
        }
      })
    );
  }


  getEmailConversationById(emailId: string): Observable<platformClient.Models.EmailConversation> {
    return from(this.conversationApi.getConversationsEmail(emailId));
  }

  getAnalyticsEmailConversationById(emailId: string): Observable<platformClient.Models.EmailConversation> {
    return from(this.conversationAnalyticsApi.getAnalyticsConversationDetails(emailId));
  }

  getEmailConversations(): Observable<platformClient.Models.EmailConversationEntityListing> {
    return from(this.conversationApi.getConversationsEmails());
  }

  declineEmailConversation(emailConversationId: string, participantId: string): Observable<any> {
    return from(this.conversationApi.patchConversationsEmailParticipant(emailConversationId, participantId, { 'state': 'DISCONNECTED' }));
  }

  disconnectEmailConversation(emailConversationId: string): Observable<any> {
    return from(this.conversationApi.postConversationDisconnect(emailConversationId));
  }
  
  disconnectCall(callConversationId: string): Observable<any> {
    return from(this.conversationApi.postConversationDisconnect(callConversationId));
  }

  declineCallConversationparticipant(callConversationId: string, participantId: string): Observable<any> {
    return from(this.conversationApi.patchConversationsCallParticipant(callConversationId, participantId, { 'state': 'disconnected' }));
  }
  
  replaceCallConversationparticipant(callConversationId: string, participantId: string): Observable<any> {
    return from(this.conversationApi.postConversationParticipantReplace(callConversationId, participantId, { 'voicemail': true }));
  }
}